<template>
  <div>
    <modal v-if="voucher" hide-footer size="xl" id="modal-voucher" @close="voucher = null">
      <template #header>
        <h3>{{ $t('words.order') }}</h3>
      </template>
      <template #body>
        <div v-if="!selectedProduct">
          <h2 class="text-center">
            {{ $t('words.voucher') }} {{ voucher.code }}
          </h2>
          <p style="text-align: center; font-weight: bold">{{ voucher.orderDetail.product.name }}</p>
          <p style="text-align: center">{{ $t('words.dayExpires') }} {{ formatDate(toDate(voucher.expiresAt)) }}</p>
          <div v-if="!isExpired()" class="text-center" style="margin-bottom: 30px">
            {{ $t('messages.availableBookings') }}: {{ availableBookings }} {{ $t('words.of') }}
            {{ voucher.maximumNumberOfBookings }}<br>
          </div>
          <div v-for="bookingOrderDetail in voucher.bookings" :key="bookingOrderDetail.id" style="text-align: center;">
            {{ $t('words.bookTheDay') }} {{ formatDateTime(toDate(bookingOrderDetail.startsAt)) }}
          </div>
          <div v-if="selectedSlot" style="margin-top: 30px;" class="text-center">

            <div style="margin-bottom: 20px"><b>{{ $t('words.bookingByDate') }}:</b>
              {{ formatDateTime(selectedSlot.startDate) }}
            </div>
            <div>
              <div class="form-field">
                <label for="wdgt-notes-field">{{ $t('words.comment') }}</label><br>
                <textarea id="wdgt-notes-field" v-model="notes">
              </textarea>
              </div>
            </div>
            <button type="button" class="secondary" @click="selectedSlot = null" style="margin-right: 30px">
              {{ $t('actions.cancel') }}
            </button>
            <button type="button" @click="sendBooking">
              {{ $t('actions.book') }}
            </button>
          </div>
          <div v-else-if="availableBookings && !isExpired()" class="text-center" style="margin-top: 30px;">
            <button type="button" class="secondary" @click="voucher = null" style="margin-right: 30px">
              {{ $t('actions.cancel') }}
            </button>
            <button type="button" @click="openCalendar">
              {{ $t('actions.book') }}
            </button>
          </div>
          <div v-else-if="isExpired()" class="text-center">
            <p style="font-weight: bold; color: #c91919">{{ $t('words.expiredOnDay').toUpperCase() }}
              {{ formatDate(toDate(voucher.expiresAt)) }}</p>
            <button type="button" class="secondary" @click="voucher = null">
              {{ $t('actions.cancel') }}
            </button>
          </div>
          <div v-else class="text-center">
            <p style="font-weight: bold; color: #c91919">{{ $t('words.valExhausted').toUpperCase() }}</p>
            <button type="button" class="secondary" @click="voucher = null">
              {{ $t('actions.cancel') }}
            </button>
          </div>
        </div>
        <SimpleCalendar v-else :product="selectedProduct" :gift-voucher="voucher"
                        @bookingSlotSelected="handleBookingSlotSelected" @cancel="handleCancel"/>
      </template>
    </modal>
    <div v-else style="background-color: #f7f7f7;padding: 10px 0">
      <div class="wdgt-image-wrapper"/>
      <div class="wdgt-name-coupon">
        <div class="wdgt-name">
          {{ $t('messages.iHaveATicketOrGiftVoucher') }}
        </div>
      </div>
      <div class="wdgt-description-input"><input v-model="voucherCode" type="text"
                                                 :placeholder="$t('words.code') + '...'" @keypress.enter="checkVoucher">
      </div>
      <div class="wdgt-buttons">
        <button type="button" @click="checkVoucher">
          {{ $t('actions.checkVoucher') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import GiftVoucherRepository from "@/api/GiftVoucherRepository";
import SimpleCalendar from '@/components/simpleCalendar';
import Modal from '@/components/modal';
import SweetAlert from "sweetalert2";
import {i18n} from "@/lang";
import filterMixin from "@/mixins/filterMixin";

export default {
  components: {SimpleCalendar, Modal},
  mixins: [filterMixin],
  data() {
    return {
      voucher: null,
      voucherCode: null,
      selectedProduct: null,
      selectedSlot: null,
      notes: ''
    }
  },
  computed: {
    availableBookings() {
      return this.voucher.maximumNumberOfBookings - this.voucher.bookings.length
    }
  },
  methods: {
    async checkVoucher() {
      try {
        if (this.voucherCode.length <= 5) {
          throw new Error('Código demasiado corto');
        }
        const response = await GiftVoucherRepository.checkVoucher(this.voucherCode)
        this.voucher = response.data;
      } catch (err) {
        await SweetAlert.fire({
          title: this.$t('messages.incorrectCode'),
          html: this.$t('messages.incorrectCodeText'),
          icon: 'info',
          iconColor: '#c91919',
          confirmButtonColor: '#c91919'
        })
      }
    },
    isExpired() {
      return (new Date(this.voucher.expiresAt) <= new Date());
    },
    openCalendar() {

      const product = this.voucher.orderDetail.product;

      // Si el producto es un pack, necesitamos encontrar el producto hijo con bookingCalendarId
      if (product.type === 'pack' && product.packItems && product.packItems.length > 0) {
        // Buscar el primer producto hijo que tenga bookingCalendarId
        const bookableProduct = product.packItems.find(
          item => item.childProduct && item.childProduct.bookingCalendarId
        );

        if (bookableProduct) {
          this.selectedProduct = bookableProduct.childProduct;
        } else {
          // Si no se encuentra producto reservable, mostrar error
          console.error('No se encontró producto reservable en el pack');
          SweetAlert.fire({
            title: this.$t('messages.error'),
            html: 'No se encontró un producto reservable en este vale de regalo',
            icon: 'error'
          });
        }
      } else {
        // Si no es un pack, usar el producto directamente
        this.selectedProduct = product;
      }
    },
    handleBookingSlotSelected(slot) {
      this.selectedSlot = slot
      this.selectedProduct = null
    },
    handleCancel() {
      this.selectedProduct = null
    },
    async sendBooking() {
      await GiftVoucherRepository.bookVoucher(
        this.voucher.code,
        this.selectedSlot.startDate,
        this.notes
      ).then((response) => {
        for (const booking of response.data) {
          this.voucher.bookings.push(booking);
        }
        this.selectedProduct = null;
        this.selectedSlot = null;
        SweetAlert.fire({
          title: this.$t('messages.bookingConfirmed'),
          html: this.$t('messages.bookingIsConfirmedText'),
          icon: 'success'
        })
      }).catch((error) => {
        if (error.response.data.code === 1000007) {
          SweetAlert.fire({
            title: i18n.t('messages.noBookingPossible'),
            html: i18n.t('messages.giftVouchersAreIndividual'),
            icon: 'info'
          });
        } else {
          SweetAlert.fire({
            title: i18n.t('messages.errorTryingToBook'),
            html: i18n.t('messages.errorTryingToBookMessage'),
            icon: 'error'
          });
        }
      })
    }
  }
}
</script>

<style scoped>
button {
  border: none;
  border-radius: 8px;
  color: #fff;
  padding: 6px 12px;
  background: #c91919;
}

button.secondary {
  background: #939393;
}
</style>
